export type HeartbeatStatus =
  | 'on_time'
  | 'overdue'
  | 'never_seen'
  | 'unknown'
  | 'paused'
  | 'unsupported';

export type OperatingMode = 'live' | 'test' | 'not_in_use';
export type ModeFilter = OperatingMode | 'all';

export const modeLabels: Record<OperatingMode, string> = {
  live: 'Live',
  test: 'Test',
  not_in_use: 'Ikke i brug',
};

export const heartbeatLabels: Record<HeartbeatStatus, string> = {
  on_time: 'Data til tiden',
  overdue: 'Data forsinket',
  never_seen: 'Ingen data registreret',
  unknown: 'Datatidspunkt ukendt',
  paused: 'Datakontrol pauset',
  unsupported: 'Ingen fast datakontrol',
};

export type HeartbeatDisplayInput = {
  status: HeartbeatStatus;
  lastDataAt: number | null;
  expectedAfter: number | null;
  evaluatedAt: number | null;
  ruleMode?: OperatingMode | null;
};

/** A recent evaluation is not a new measurement. Never turn stale evidence green. */
export function heartbeatPresentation(value: HeartbeatDisplayInput, now = Date.now()) {
  const evaluationStale =
    value.evaluatedAt === null ||
    value.evaluatedAt > now + 5 * 60_000 ||
    now - value.evaluatedAt > 60 * 60_000;
  // A cached on-time judgment can pass its deadline before the next refresh.
  const status =
    value.status === 'on_time' && (value.lastDataAt === null || value.lastDataAt > now + 5 * 60_000)
      ? 'unknown'
      : value.status === 'on_time' && value.expectedAfter !== null && now > value.expectedAfter
      ? 'overdue'
      : value.status;
  const unconfirmed = status === 'on_time' && evaluationStale;
  return {
    status,
    evaluationStale,
    label: `${value.ruleMode === 'test' ? 'Test · ' : ''}${
      unconfirmed ? 'Senest: data til tiden' : heartbeatLabels[status]
    }`,
    color:
      value.ruleMode === 'test'
        ? 'blue'
        : status === 'on_time' && !evaluationStale
        ? 'green'
        : status === 'overdue' || status === 'never_seen'
        ? 'orange'
        : undefined,
  };
}

export function matchesOperatingMode(mode: OperatingMode | null | undefined, filter: ModeFilter) {
  return filter === 'all' || mode === filter;
}

export function heartbeatTime(value: number | null | undefined) {
  if (value === 0) return 'Ikke vurderet';
  return value === null || value === undefined
    ? 'Ukendt'
    : new Date(value).toLocaleString('da-DK', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Copenhagen',
      });
}
